// Personal Drive — full-screen file viewer (Google-Drive-style lightbox).
//
// Click a file card → open here. Images open in a dark full-screen lightbox that
// fits to the viewport by default and zooms to actual size (1:1) / arbitrary
// scale, panning via native scroll when larger than the viewport. PDFs render in
// a full-size iframe; other docs offer a download. The presigned URL is minted
// lazily via the shared audited /drive/files/{id}/download-url endpoint (real
// mode); maket mode shows a simulated placeholder.

import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { Download, FileText, ImageIcon, Minus, Plus, X } from 'lucide-react'
import { USE_REAL_API } from '../../lib/api'
import { fetchFileViewUrl, type PersonalDriveFile } from '../../api/drive-personal'
import { useT } from '../../i18n'
import { drivePersonalI18n } from '../../i18n/shared/drive-personal'
import { drivePersonalExtraI18n } from '../../i18n/drive/personal-extra'
import { toast } from '../../lib/toast'

type DriveT = (typeof drivePersonalI18n)['en']

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

export function PersonalFilePreview({
  file,
  onClose,
}: {
  file: PersonalDriveFile | null
  onClose: () => void
}) {
  const t = useT(drivePersonalI18n)
  // Presigned preview URL — minted lazily, only in real-engine mode. TTL ~1h so
  // we cache it for the session.
  const viewQuery = useQuery({
    queryKey: ['drive-personal', 'view-url', file?.id],
    queryFn: () => fetchFileViewUrl(file!.id, 'preview'),
    enabled: file != null && USE_REAL_API,
    staleTime: 50 * 60 * 1000,
    retry: false,
  })
  const url = viewQuery.data?.download_url ?? null
  const loading = USE_REAL_API && viewQuery.isLoading
  const errored = USE_REAL_API && viewQuery.isError

  const onDownload = async () => {
    if (!file) return
    if (!USE_REAL_API) {
      toast.info(t.previewMockNote)
      return
    }
    try {
      const r = await fetchFileViewUrl(file.id, 'download')
      window.open(r.download_url, '_blank', 'noopener')
    } catch {
      toast.error(t.loadError)
    }
  }

  return (
    <Dialog.Root open={file != null} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed inset-0 z-[60] flex flex-col outline-none"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {file && (
            <Viewer
              key={file.id}
              file={file}
              url={url}
              loading={loading}
              errored={errored}
              onClose={onClose}
              onDownload={onDownload}
              t={t}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// Keyed by file.id so zoom state resets cleanly when switching files.
function Viewer(props: {
  file: PersonalDriveFile
  url: string | null
  loading: boolean
  errored: boolean
  onClose: () => void
  onDownload: () => void
  t: DriveT
}) {
  const { file, url, loading, errored, onClose, onDownload, t } = props
  const tx = useT(drivePersonalExtraI18n)
  const [fit, setFit] = useState(true)
  const [scale, setScale] = useState(1)
  const [natW, setNatW] = useState<number | null>(null)

  const isImage = file.kind === 'image'
  const isPdf = file.kind === 'pdf'
  const showZoom = isImage && USE_REAL_API && !!url && !loading && !errored

  const toggleActual = () => {
    setFit((f) => !f)
    setScale(1)
  }
  const zoomIn = () => {
    setFit(false)
    setScale((s) => Math.min(6, +(s + 0.25).toFixed(2)))
  }
  const zoomOut = () => {
    setFit(false)
    setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))
  }

  return (
    <div className="flex h-full flex-col">
      {/* Top bar */}
      <div className="flex shrink-0 items-center gap-2 px-3 py-2.5 text-white sm:px-4">
        <Dialog.Title className="min-w-0 flex-1 truncate text-sm font-medium" title={file.name}>
          {file.name}
        </Dialog.Title>
        <span className="hidden text-xs tabular-nums text-white/55 sm:inline">
          {humanFileSize(file.size_bytes)}
        </span>
        {showZoom && (
          <div className="mx-1 flex items-center gap-0.5 rounded-lg bg-white/10 p-0.5">
            <IconBtn onClick={zoomOut} label={tx.zoomOut}>
              <Minus className="h-4 w-4" />
            </IconBtn>
            <button
              type="button"
              onClick={toggleActual}
              className="rounded px-2 py-1 text-xs font-semibold text-white/90 hover:bg-white/15"
            >
              {fit ? '100%' : tx.fit}
            </button>
            <IconBtn onClick={zoomIn} label={tx.zoomIn}>
              <Plus className="h-4 w-4" />
            </IconBtn>
          </div>
        )}
        <IconBtn onClick={onDownload} label={t.download}>
          <Download className="h-4 w-4" />
        </IconBtn>
        <IconBtn onClick={onClose} label={t.cancel}>
          <X className="h-5 w-5" />
        </IconBtn>
      </div>

      {/* Body */}
      <div className="relative flex-1 overflow-auto">
        {loading ? (
          <Centered>
            <span className="text-sm text-white/70">…</span>
          </Centered>
        ) : errored ? (
          <Centered>
            <span className="text-sm text-rose-300">{t.loadError}</span>
          </Centered>
        ) : !USE_REAL_API ? (
          <Centered>
            <div className="flex flex-col items-center gap-2 text-white/70">
              {isImage ? <ImageIcon className="h-14 w-14" /> : <FileText className="h-14 w-14" />}
              <p className="text-sm font-medium text-white/90">{file.name}</p>
              <p className="text-xs">{t.previewMockNote}</p>
            </div>
          </Centered>
        ) : isImage && url ? (
          <div className="flex min-h-full items-center justify-center p-4">
            <img
              src={url}
              alt={file.name}
              onLoad={(e) => setNatW(e.currentTarget.naturalWidth)}
              onClick={toggleActual}
              className={
                fit
                  ? 'max-h-[calc(100vh-5.5rem)] max-w-full cursor-zoom-in object-contain'
                  : 'cursor-zoom-out'
              }
              style={
                fit ? undefined : { width: natW ? `${natW * scale}px` : 'auto', maxWidth: 'none' }
              }
            />
          </div>
        ) : isPdf && url ? (
          <iframe src={url} title={file.name} className="h-full w-full border-0 bg-white" />
        ) : (
          <Centered>
            <div className="flex flex-col items-center gap-3 text-white/70">
              <FileText className="h-14 w-14 text-white/40" />
              <p className="text-sm">{t.previewNoInline}</p>
              <button
                type="button"
                onClick={onDownload}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                <Download className="h-3.5 w-3.5" /> {t.download}
              </button>
            </div>
          </Centered>
        )}
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-full items-center justify-center p-8">{children}</div>
}

function IconBtn({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded-lg p-2 text-white/80 transition hover:bg-white/15 hover:text-white"
    >
      {children}
    </button>
  )
}
